import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import './DataAnalysis.css'

const PvcPipesQuery = () => {
    const [pipes, setPipes] = useState([])
    const [loading, setLoading] = useState(true)
    const [query, setQuery] = useState('')
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

    const [errorMsg, setErrorMsg] = useState(null)

    // 判斷是否手機
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // 載入 PVC 管徑資料
    useEffect(() => {
        loadPipesData()
    }, [])

    const loadPipesData = async () => {
        try {
            setLoading(true)
            setErrorMsg(null)
            console.log("正在載入 PVC 管徑資料 from pvc_pipes...")

            const { data, error } = await supabase.from('pvc_pipes').select('*')

            if (error) {
                console.error('載入管徑資料失敗 API Error:', error)
                throw error
            }

            if (!data || data.length === 0) {
                console.warn("未找到管徑資料 (data is empty)")
                setErrorMsg("查詢成功，但資料庫中沒有資料 (Table is empty or RLS policy restricts access)")
            }

            setPipes(data || [])
        } catch (error) {
            console.error('載入管徑資料失敗:', error)
            setErrorMsg(`讀取失敗: ${error.message || '未知錯誤'} (Code: ${error.code})`)
        } finally {
            setLoading(false)
        }
    }

    // 搜尋與排序邏輯
    const filteredPipes = pipes
        .filter(p =>
            (p.pipe_inch && p.pipe_inch.toLowerCase().includes(query.toLowerCase()))
        )
        .sort((a, b) => {
            // 按 id 排序
            return (parseInt(a.id) || 0) - (parseInt(b.id) || 0)
        })

    if (loading) {
        return (
            <div className="data-analysis">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>正在載入管徑資料...</p>
                </div>
            </div>
        )
    }

    if (errorMsg) {
        return (
            <div className="data-analysis">
                <div className="error-container" style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                    <h3>⚠️ 發生錯誤</h3>
                    <p>{errorMsg}</p>
                    <p style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
                        請檢查 Supabase 資料庫中的 <code>pvc_pipes</code> 資料表是否存在，以及 RLS (Row Level Security) 設定是否允許讀取。
                    </p>
                    <button className="refresh-btn" onClick={loadPipesData} style={{ marginTop: '15px' }}>
                        重試
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pvc-pipes-page">
            <div className="page-header">
                <h2>PVC-U塑膠管</h2>
                <p>(自來水用 CNS4053-1)</p>
            </div>

            {/* 搜尋與排序 */}
            <div className="search-controls">
                <div className="search-input-container">
                    <input
                        type="text"
                        placeholder="搜尋管徑英吋 (例如 3/4 或 1)"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="search-input"
                    />
                </div>


            </div>

            {/* 結果統計 */}
            <div className="results-info">
                <p>找到 {filteredPipes.length} 個結果 (共 {pipes.length} 種管材)</p>
            </div>

            {/* 資料顯示 */}
            {isMobile ? (
                // 手機卡片模式
                <div className="pipes-grid-mobile">
                    {filteredPipes.map((pipe) => (
                        <div key={pipe.id} className="pipe-card">
                            <div className="pipe-card-header">
                                <h3>{pipe.pipe_inch || '未知'}英吋</h3>
                                <span className="pipe-code">({pipe.pipe_code || '未知'})</span>
                            </div>
                            <div className="pipe-card-content">
                                <div className="pipe-spec">
                                    <span className="spec-label">外徑：</span>
                                    <span className="spec-value">{pipe.outer_diameter_mm || 'N/A'} mm</span>
                                </div>
                                <div className="pipe-spec">
                                    <span className="spec-label">內徑：</span>
                                    <span className="spec-value">{pipe.inner_diameter_mm || 'N/A'} mm</span>
                                </div>
                                <div className="pipe-spec">
                                    <span className="spec-label">厚度：</span>
                                    <span className="spec-value">{pipe.thickness_mm || 'N/A'} mm</span>
                                </div>
                                <div className="pipe-spec">
                                    <span className="spec-label">最小厚度：</span>
                                    <span className="spec-value">{pipe.min_thickness_mm || 'N/A'} mm</span>
                                </div>
                                <div className="pipe-spec">
                                    <span className="spec-label">長度：</span>
                                    <span className="spec-value">{pipe.length_m || 'N/A'} m</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                // 桌機表格模式
                <div className="pipes-table-container">
                    <table className="pipes-table">
                        <thead>
                            <tr>
                                <th>英吋</th>
                                <th>管代號</th>
                                <th>外徑 (mm)</th>
                                <th>內徑 (mm)</th>
                                <th>厚度 (mm)</th>
                                <th>最小厚度</th>
                                <th>長度 (m)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPipes.map((pipe) => (
                                <tr key={pipe.id}>
                                    <td className="pipe-inch">{pipe.pipe_inch || 'N/A'}</td>
                                    <td className="pipe-code">{pipe.pipe_code || 'N/A'}</td>
                                    <td>{pipe.outer_diameter_mm || 'N/A'}</td>
                                    <td>{pipe.inner_diameter_mm || 'N/A'}</td>
                                    <td>{pipe.thickness_mm || 'N/A'}</td>
                                    <td>{pipe.min_thickness_mm || 'N/A'}</td>
                                    <td>{pipe.length_m || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

export default PvcPipesQuery
